import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { IoCubeOutline, IoCheckmark, IoClose, IoEarthOutline, IoDocumentTextOutline, IoLinkOutline, IoCopyOutline } from 'react-icons/io5';
import { addPackagingUnitComponent, updatePackagingUnitComponent, deletePackagingUnitComponent } from '../../../../store/actions/packagingUnit';
import { fetchClients } from '../../../../store/actions/client';
import { EU_COUNTRIES } from '../../../../utils/constants/euCountries';
import { COUNTRIES } from '../../../../utils/constants/countries';
import { PACKAGING_MATERIAL_LABELS, PACKAGING_MATERIAL_GROUPED_OPTIONS } from '../../../../utils/constants/packagingMaterials';
import { BASE_LANGUAGE, getMarketLanguages } from '../../../../utils/constants/marketLanguages';
import LanguageSwitch from '../languageSwitch';
import { ReactComponent as RemoveIcon } from '../../../../assets/remove.svg';
import { ReactComponent as EditIcon } from '../../../../assets/edit.svg';
import CustomModalLarge from '../../../../hoc/customModalLarge';
import AddClient from '../../../client/add';

const compactSelectStyles = {
    control: (base) => ({ ...base, minHeight: 31, fontSize: 13 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: 31 }),
    menu: (base) => ({ ...base, fontSize: 13 }),
    menuPortal: (base) => ({ ...base, zIndex: 3000 }),
    groupHeading: (base) => ({ ...base, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }),
};

const countryLabel = (code) => EU_COUNTRIES.find(c => c.code === code)?.label ?? code;

// Vráti text komponentu v aktuálne zobrazenom jazyku (viď prepínač v hlavičke tabuľky) - ak preklad
// pre daný jazyk chýba, spadne späť na slovenský text a označí ho ako nepreložený (zobrazí sa kurzívou).
const getDisplayText = (component, field, lang) => {
    if (lang === BASE_LANGUAGE) return { text: component[field], translated: true };
    const translated = component.translations?.[lang]?.[field];
    return translated ? { text: translated, translated: true } : { text: component[field], translated: false };
};

const PAGE_SIZE = 10;

const getPageRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
    if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '…', current - 1, current, current + 1, '…', total];
};

const EMPTY_COMPONENT = {
    component: '', material: '', weight: '', recyclable: false, foodContact: false,
    supplier: '', countryOfOrigin: '', note: '', translations: {},
};

const Section = ({ icon: Icon, title, children }) => (
    <div className="h-100 p-3 d-flex flex-column" style={{ backgroundColor: '#ffffff', border: '1px solid #E5E7F2', borderRadius: 14 }}>
        <div className="d-flex align-items-center gap-2 pb-2 mb-2" style={{ borderBottom: '1px solid #EEF0F8' }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, backgroundColor: '#EEF1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={14} style={{ color: '#0B1157' }} />
            </div>
            <span className="fw-semibold" style={{ color: '#0B1157', fontSize: 13 }}>{title}</span>
        </div>
        <div className="flex-grow-1">{children}</div>
    </div>
);

const Field = ({ label, children, last }) => (
    <div className={last ? '' : 'mb-2'}>
        <div className="text-muted" style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 13.5, color: children ? '#0B1157' : '#adb5bd', fontStyle: children ? 'normal' : 'italic' }}>
            {children ?? 'Neuvedené'}
        </div>
    </div>
);

const Chip = ({ active, label }) => (
    <span
        className="d-inline-flex align-items-center gap-1"
        style={{
            fontSize: 11.5,
            fontWeight: 600,
            padding: '3px 9px',
            borderRadius: 999,
            backgroundColor: active ? '#E6F4EA' : '#F1F2F6',
            color: active ? '#198754' : '#9AA0B4',
        }}
    >
        {active ? <IoCheckmark size={12} /> : <IoClose size={12} />}
        {label}
    </span>
);

const BoolCell = ({ active }) => (
    active
        ? <IoCheckmark size={16} style={{ color: '#198754' }} />
        : <IoClose size={16} style={{ color: '#c3c7dc' }} />
);

const td = { padding: '8px 10px', verticalAlign: 'middle' };

const ComponentFormRow = ({ values, onChange, onSave, onCancel, saving, clients, onAddClient, autoFocus, lang }) => {
    const isTranslating = lang !== BASE_LANGUAGE;

    const getTranslated = (field) => (
        lang === BASE_LANGUAGE ? values[field] : (values.translations?.[lang]?.[field] ?? '')
    );
    const setTranslated = (field, value) => {
        if (lang === BASE_LANGUAGE) {
            onChange(field, value);
        } else {
            onChange('translations', { ...values.translations, [lang]: { ...values.translations?.[lang], [field]: value } });
        }
    };

    return (
    <tr style={{ background: '#F4F5FE', borderTop: '1px solid #EEF0F8' }}>
        <td style={td} />
        <td style={td}>
            <Form.Control
                size="sm"
                autoFocus={autoFocus}
                placeholder="Napr. veko"
                value={getTranslated('component')}
                onChange={e => setTranslated('component', e.target.value)}
            />
        </td>
        <td style={{ ...td, minWidth: 200 }}>
            <Select
                options={PACKAGING_MATERIAL_GROUPED_OPTIONS}
                value={values.material ? { value: values.material, label: PACKAGING_MATERIAL_LABELS[values.material] } : null}
                onChange={(selected) => onChange('material', selected ? selected.value : '')}
                placeholder="Vyberte materiál..."
                isClearable
                isDisabled={isTranslating}
                noOptionsMessage={() => 'Žiadne výsledky'}
                styles={compactSelectStyles}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                maxMenuHeight={240}
            />
        </td>
        <td style={{ ...td, minWidth: 90 }}>
            <Form.Control
                size="sm"
                type="number"
                min="0"
                value={values.weight}
                onChange={e => onChange('weight', e.target.value)}
                disabled={isTranslating}
            />
        </td>
        <td style={{ ...td, textAlign: 'center' }}>
            <Form.Check
                checked={values.recyclable}
                onChange={e => onChange('recyclable', e.target.checked)}
                disabled={isTranslating}
            />
        </td>
        <td style={{ ...td, textAlign: 'center' }}>
            <Form.Check
                checked={values.foodContact}
                onChange={e => onChange('foodContact', e.target.checked)}
                disabled={isTranslating}
            />
        </td>
        <td style={{ ...td, minWidth: 200 }}>
            <div className="d-flex gap-1">
                <div className="flex-grow-1">
                    <Select
                        options={clients.map(client => ({ value: client.companyName, label: client.companyName }))}
                        value={values.supplier ? { value: values.supplier, label: values.supplier } : null}
                        onChange={(selected) => onChange('supplier', selected ? selected.value : '')}
                        placeholder="Vyhľadať dodávateľa..."
                        isClearable
                        isDisabled={isTranslating}
                        noOptionsMessage={() => 'Žiadne výsledky'}
                        styles={compactSelectStyles}
                        menuPortalTarget={document.body}
                        menuPlacement="auto"
                        maxMenuHeight={240}
                    />
                </div>
                {!isTranslating && (
                    <OverlayTrigger placement="top" overlay={<Tooltip>Pridať dodávateľa</Tooltip>}>
                        <Button
                            variant="outline-secondary"
                            type="button"
                            className="flex-shrink-0"
                            onClick={onAddClient}
                        >
                            +
                        </Button>
                    </OverlayTrigger>
                )}
            </div>
        </td>
        <td style={{ ...td, minWidth: 180 }}>
            <Select
                options={COUNTRIES}
                value={values.countryOfOrigin ? { value: values.countryOfOrigin, label: values.countryOfOrigin } : null}
                onChange={(selected) => onChange('countryOfOrigin', selected ? selected.value : '')}
                placeholder="Vyhľadať krajinu..."
                isClearable
                isDisabled={isTranslating}
                noOptionsMessage={() => 'Žiadne výsledky'}
                styles={compactSelectStyles}
                menuPortalTarget={document.body}
                menuPlacement="auto"
                maxMenuHeight={240}
            />
        </td>
        <td style={td}>
            <Form.Control
                size="sm"
                value={getTranslated('note')}
                onChange={e => setTranslated('note', e.target.value)}
            />
        </td>
        <td style={{
            ...td,
            textAlign: 'right', whiteSpace: 'nowrap',
            position: 'sticky', right: 0,
            background: '#F4F5FE',
            boxShadow: '-4px 0 8px rgba(11,17,87,0.07)',
        }}>
            <Button
                size="sm"
                variant="success"
                className="me-1"
                disabled={!values.component.trim() || !values.material || !values.countryOfOrigin || saving}
                onClick={onSave}
            >
                Uložiť
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={onCancel}>
                Zrušiť
            </Button>
        </td>
    </tr>
    );
};

const Overview = ({ unit }) => {
    const dispatch = useDispatch();
    const clients = useSelector(state => state.client.data);

    const [isAdding, setIsAdding] = useState(false);
    const [newComponent, setNewComponent] = useState(EMPTY_COMPONENT);
    const [savingComponent, setSavingComponent] = useState(false);

    const [editingComponentId, setEditingComponentId] = useState(null);
    const [editComponent, setEditComponent] = useState(EMPTY_COMPONENT);
    const [savingEdit, setSavingEdit] = useState(false);

    const [componentToDelete, setComponentToDelete] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [page, setPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [isAddClientModalVisible, setIsAddClientModalVisible] = useState(false);
    const [addClientTarget, setAddClientTarget] = useState(null); // 'add' | 'edit'

    useEffect(() => {
        dispatch(fetchClients('PACKAGING'));
    }, [dispatch]);

    const marketLanguages = useMemo(() => getMarketLanguages(unit.marketCountries), [unit.marketCountries]);
    const [tableLang, setTableLang] = useState(BASE_LANGUAGE);
    const activeTableLang = marketLanguages.includes(tableLang) || tableLang === BASE_LANGUAGE ? tableLang : BASE_LANGUAGE;
    const [propsLang, setPropsLang] = useState(BASE_LANGUAGE);
    const activePropsLang = marketLanguages.includes(propsLang) || propsLang === BASE_LANGUAGE ? propsLang : BASE_LANGUAGE;

    const updateNewComponent = (field, value) => setNewComponent(c => ({ ...c, [field]: value }));
    const updateEditComponent = (field, value) => setEditComponent(c => ({ ...c, [field]: value }));

    const handleClientCreated = async (clientId) => {
        try {
            const updatedClients = await dispatch(fetchClients('PACKAGING')).unwrap();
            const newClient = Array.isArray(updatedClients) ? updatedClients.find(c => c._id === clientId) : null;
            if (newClient) {
                if (addClientTarget === 'edit') updateEditComponent('supplier', newClient.companyName);
                else updateNewComponent('supplier', newClient.companyName);
            }
        } finally {
            setIsAddClientModalVisible(false);
            setAddClientTarget(null);
        }
    };

    const allComponents = unit.components ?? [];
    const totalPages = Math.ceil(allComponents.length / PAGE_SIZE);
    const safePage = Math.min(page, totalPages || 1);
    const pagedComponents = allComponents.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const pageRange = getPageRange(safePage, totalPages);

    const toggleSelect = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const allOnPageSelected = pagedComponents.length > 0 && pagedComponents.every(c => selectedIds.has(c._id));
    const someOnPageSelected = pagedComponents.some(c => selectedIds.has(c._id));

    const toggleSelectAll = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allOnPageSelected) {
                pagedComponents.forEach(c => next.delete(c._id));
            } else {
                pagedComponents.forEach(c => next.add(c._id));
            }
            return next;
        });
    };

    const handleBulkDelete = async () => {
        setBulkDeleting(true);
        try {
            for (const componentId of selectedIds) {
                await dispatch(deletePackagingUnitComponent({ packagingUnitId: unit._id, componentId })).unwrap();
            }
            setSelectedIds(new Set());
            setShowBulkDeleteModal(false);
        } catch {
            // chyba zobrazená cez globálny error toast
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleCopy = (c) => {
        setEditingComponentId(null);
        setNewComponent({
            component: c.component ?? '',
            material: c.material ?? '',
            weight: c.weight ?? '',
            recyclable: !!c.recyclable,
            foodContact: !!c.foodContact,
            supplier: c.supplier ?? '',
            countryOfOrigin: c.countryOfOrigin ?? '',
            note: c.note ?? '',
            translations: c.translations ?? {},
        });
        setIsAdding(true);
        setPage(1);
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setNewComponent(EMPTY_COMPONENT);
    };

    const handleSaveComponent = () => {
        if (!newComponent.component.trim()) return;
        setSavingComponent(true);
        dispatch(addPackagingUnitComponent({ packagingUnitId: unit._id, values: newComponent }))
            .unwrap()
            .then(() => {
                setNewComponent(EMPTY_COMPONENT);
                setIsAdding(false);
            })
            .catch(() => {})
            .finally(() => setSavingComponent(false));
    };

    const handleStartEdit = (c) => {
        setIsAdding(false);
        setEditingComponentId(c._id);
        setEditComponent({
            component: c.component ?? '',
            material: c.material ?? '',
            weight: c.weight ?? '',
            recyclable: !!c.recyclable,
            foodContact: !!c.foodContact,
            supplier: c.supplier ?? '',
            countryOfOrigin: c.countryOfOrigin ?? '',
            note: c.note ?? '',
            translations: c.translations ?? {},
        });
    };

    const handleCancelEdit = () => {
        setEditingComponentId(null);
        setEditComponent(EMPTY_COMPONENT);
    };

    const handleSaveEdit = () => {
        if (!editComponent.component.trim()) return;
        setSavingEdit(true);
        dispatch(updatePackagingUnitComponent({ packagingUnitId: unit._id, componentId: editingComponentId, values: editComponent }))
            .unwrap()
            .then(() => {
                setEditingComponentId(null);
                setEditComponent(EMPTY_COMPONENT);
            })
            .catch(() => {})
            .finally(() => setSavingEdit(false));
    };

    const handleDeleteComponent = () => {
        if (!componentToDelete) return;
        dispatch(deletePackagingUnitComponent({ packagingUnitId: unit._id, componentId: componentToDelete._id }))
            .unwrap()
            .then(() => setComponentToDelete(null))
            .catch(() => setComponentToDelete(null));
    };

    return (
        <div>
            <Row className="g-2 mb-3 align-items-stretch">
                <Col xs={12} md={4}>
                    <Section icon={IoCubeOutline} title="Vlastnosti obalu">
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            <Chip active={unit.foodContact} label="Kontakt s potravinou" />
                            <Chip active={unit.recyclable} label="Recyklovateľný" />
                            <Chip active={unit.deposit} label="Zálohovaný" />
                        </div>
                        {marketLanguages.length > 0 && (
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <span style={{ fontSize: 10.5, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Zobrazený jazyk textov
                                </span>
                                <LanguageSwitch languages={marketLanguages} active={activePropsLang} onChange={setPropsLang} size="sm" />
                            </div>
                        )}
                        <Field label="Použitie">
                            {(() => { const d = getDisplayText(unit, 'usage', activePropsLang); return d.text ? (
                                <span style={!d.translated ? { fontStyle: 'italic', color: '#9AA0B4' } : undefined} title={!d.translated ? 'Preklad chýba, zobrazený je slovenský text' : undefined}>{d.text}</span>
                            ) : undefined; })()}
                        </Field>
                        <Field label="Popis obalu">
                            {(() => { const d = getDisplayText(unit, 'description', activePropsLang); return d.text ? (
                                <span style={!d.translated ? { fontStyle: 'italic', color: '#9AA0B4' } : undefined} title={!d.translated ? 'Preklad chýba, zobrazený je slovenský text' : undefined}>{d.text}</span>
                            ) : undefined; })()}
                        </Field>
                        <Field label="Opis a vysvetlenie fungovania obalu" last>
                            {(() => { const d = getDisplayText(unit, 'operationDescription', activePropsLang); return d.text ? (
                                <span style={!d.translated ? { fontStyle: 'italic', color: '#9AA0B4' } : undefined} title={!d.translated ? 'Preklad chýba, zobrazený je slovenský text' : undefined}>{d.text}</span>
                            ) : undefined; })()}
                        </Field>
                    </Section>
                </Col>

                <Col xs={12} md={4}>
                    <Section icon={IoEarthOutline} title="Trh a uvedenie na trh">
                        <Field label="Trh uvedenia">
                            {Array.isArray(unit.marketCountries) && unit.marketCountries.length > 0 ? (
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    {unit.marketCountries.map(code => (
                                        <span key={code} className="badge bg-light text-dark border">{countryLabel(code)}</span>
                                    ))}
                                </div>
                            ) : null}
                        </Field>
                        <Field label="Dátum uvedenia na trh" last>
                            {unit.marketDate ? new Date(unit.marketDate).toLocaleDateString('sk-SK') : null}
                        </Field>
                    </Section>
                </Col>

                <Col xs={12} md={4}>
                    <Section icon={IoDocumentTextOutline} title="Identifikácia a dokumentácia">
                        <Field label="EAN / Interný kód">{unit.code}</Field>
                        <Field label="GTIN obalu">{unit.gtin}</Field>
                        <Field label="Externý digitálny pas">
                            {unit.digitalPassportUrl ? (
                                <a href={unit.digitalPassportUrl} target="_blank" rel="noreferrer" className="d-inline-flex align-items-center gap-1">
                                    <IoLinkOutline size={13} /> {unit.digitalPassportUrl}
                                </a>
                            ) : null}
                        </Field>
                        <Field label="Interná poznámka" last>{unit.internalNote}</Field>
                    </Section>
                </Col>
            </Row>

            <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="fw-semibold" style={{ color: '#0B1157', fontSize: 14 }}>Komponenty obalovej jednotky</span>
                    {!isAdding && (
                        <Button
                            className="btn-custom-small"
                            onClick={() => { setEditingComponentId(null); setIsAdding(true); setPage(1); }}
                        >
                            + Pridať komponent
                        </Button>
                    )}
                </div>

                {selectedIds.size > 0 && (
                    <div
                        className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2"
                        style={{ background: '#0B1157', borderRadius: 10, padding: '8px 14px' }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                            {selectedIds.size} {selectedIds.size === 1 ? 'komponent vybraný' : selectedIds.size < 5 ? 'komponenty vybrané' : 'komponentov vybraných'}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                onClick={() => setSelectedIds(new Set())}
                                style={{ border: 'none', borderRadius: 7, padding: '4px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', background: 'rgba(255,255,255,0.12)', color: '#fff' }}
                            >
                                Zrušiť výber
                            </button>
                            <button
                                onClick={() => setShowBulkDeleteModal(true)}
                                style={{ border: 'none', borderRadius: 7, padding: '4px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', background: '#dc3545', color: '#fff' }}
                            >
                                Vymazať vybrané
                            </button>
                        </div>
                    </div>
                )}

                <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                    <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
                        <thead>
                            {marketLanguages.length > 0 && (
                                <tr style={{ background: '#EEF1FB' }}>
                                    <th colSpan={10} style={{ padding: '6px 10px' }}>
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{ fontSize: 10.5, fontWeight: 700, color: '#5B6178', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Zobrazený jazyk textov
                                            </span>
                                            <LanguageSwitch languages={marketLanguages} active={activeTableLang} onChange={setTableLang} />
                                        </div>
                                    </th>
                                </tr>
                            )}
                            <tr style={{ background: '#F4F5FE' }}>
                                <th style={{ padding: '8px 10px', width: 36, textAlign: 'center' }}>
                                    <Form.Check
                                        checked={allOnPageSelected}
                                        ref={el => { if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected; }}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left' }}>Komponent <span style={{ color: '#dc3545' }}>*</span></th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left' }}>Materiál <span style={{ color: '#dc3545' }}>*</span></th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' }}>Hmotnosť (g)</th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'center', whiteSpace: 'nowrap' }}>Recyklovateľnosť</th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'center', whiteSpace: 'nowrap' }}>Kontakt s potravinou</th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left' }}>Dodávateľ</th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' }}>Krajina pôvodu <span style={{ color: '#dc3545' }}>*</span></th>
                                <th style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' }}>Interná poznámka</th>
                                <th style={{ padding: '8px 10px', width: 80 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {isAdding && (
                                <ComponentFormRow
                                    values={newComponent}
                                    onChange={updateNewComponent}
                                    onSave={handleSaveComponent}
                                    onCancel={handleCancelAdd}
                                    saving={savingComponent}
                                    clients={clients}
                                    onAddClient={() => { setAddClientTarget('add'); setIsAddClientModalVisible(true); }}
                                    lang={activeTableLang}
                                    autoFocus
                                />
                            )}

                            {unit.components && unit.components.length > 0 ? pagedComponents.map((c, i) => (
                                editingComponentId === c._id ? (
                                    <ComponentFormRow
                                        key={c._id}
                                        values={editComponent}
                                        onChange={updateEditComponent}
                                        onSave={handleSaveEdit}
                                        onCancel={handleCancelEdit}
                                        saving={savingEdit}
                                        clients={clients}
                                        onAddClient={() => { setAddClientTarget('edit'); setIsAddClientModalVisible(true); }}
                                        lang={activeTableLang}
                                    />
                                ) : (
                                    <tr
                                        key={c._id}
                                        onMouseEnter={() => setHoveredRow(c._id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        style={{
                                            background: hoveredRow === c._id ? '#EEF1FB' : ((safePage - 1) * PAGE_SIZE + i) % 2 === 0 ? '#fff' : '#F9FAFF',
                                            borderTop: '1px solid #EEF0F8',
                                        }}
                                    >
                                        <td style={{ padding: '10px', textAlign: 'center', verticalAlign: 'middle' }}>
                                            <Form.Check
                                                checked={selectedIds.has(c._id)}
                                                onChange={() => toggleSelect(c._id)}
                                            />
                                        </td>
                                        <td style={{ padding: '10px', fontSize: 13, fontWeight: 600, color: '#0B1157', verticalAlign: 'middle' }}>
                                            {(() => { const d = getDisplayText(c, 'component', activeTableLang); return (
                                                <span style={!d.translated ? { fontStyle: 'italic', color: '#9AA0B4', fontWeight: 400 } : undefined} title={!d.translated ? 'Preklad chýba, zobrazený je slovenský text' : undefined}>{d.text}</span>
                                            ); })()}
                                        </td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#0B1157', verticalAlign: 'middle' }}>{c.material ? (PACKAGING_MATERIAL_LABELS[c.material] ?? c.material) : '—'}</td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#0B1157', verticalAlign: 'middle' }}>{c.weight != null && c.weight !== '' ? `${c.weight} g` : '—'}</td>
                                        <td style={{ padding: '10px', textAlign: 'center', verticalAlign: 'middle' }}><BoolCell active={c.recyclable} /></td>
                                        <td style={{ padding: '10px', textAlign: 'center', verticalAlign: 'middle' }}><BoolCell active={c.foodContact} /></td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#0B1157', verticalAlign: 'middle' }}>{c.supplier || '—'}</td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#0B1157', verticalAlign: 'middle' }}>{c.countryOfOrigin || '—'}</td>
                                        <td style={{ padding: '10px', fontSize: 13, color: '#0B1157', verticalAlign: 'middle' }}>
                                            {(() => { const d = getDisplayText(c, 'note', activeTableLang); return d.text ? (
                                                <span style={!d.translated ? { fontStyle: 'italic', color: '#9AA0B4' } : undefined} title={!d.translated ? 'Preklad chýba, zobrazený je slovenský text' : undefined}>{d.text}</span>
                                            ) : '—'; })()}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                            <Button className="btnIcon" onClick={() => handleStartEdit(c)}>
                                                <EditIcon className="icon icon-edit" alt="Upraviť" />
                                            </Button>
                                            <Button className="btnIcon" onClick={() => handleCopy(c)}>
                                                <IoCopyOutline className="icon" size={16} style={{ color: '#718094' }} />
                                            </Button>
                                            <Button className="btnIcon" onClick={() => setComponentToDelete(c)}>
                                                <RemoveIcon className="icon icon-remove" alt="Vymazať" />
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            )) : !isAdding && (
                                <tr>
                                    <td
                                        colSpan={10}
                                        style={{ padding: '24px 12px', textAlign: 'center', color: '#9AA0B4', fontSize: 13 }}
                                    >
                                        Zatiaľ žiadne komponenty.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="d-flex align-items-center justify-content-between mt-2 flex-wrap gap-2">
                        <span style={{ fontSize: 12, color: '#9AA0B4' }}>
                            {allComponents.length} komponentov · strana {safePage} z {totalPages}
                        </span>
                        <div className="d-flex gap-1">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={safePage === 1}
                                style={{
                                    border: 'none', borderRadius: 8, padding: '4px 10px',
                                    fontSize: 13, fontWeight: 600, cursor: safePage === 1 ? 'default' : 'pointer',
                                    backgroundColor: '#F1F2F6',
                                    color: safePage === 1 ? '#C8CBDA' : '#5B6178',
                                }}
                            >
                                ‹
                            </button>
                            {pageRange.map((p, i) =>
                                p === '…' ? (
                                    <span key={`ellipsis-${i}`} style={{ padding: '4px 6px', fontSize: 13, color: '#9AA0B4', alignSelf: 'center' }}>…</span>
                                ) : (
                                    <button
                                        key={p}
                                        onClick={() => setPage(p)}
                                        style={{
                                            border: 'none', borderRadius: 8, padding: '4px 10px',
                                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                            minWidth: 32,
                                            backgroundColor: safePage === p ? '#0B1157' : '#F1F2F6',
                                            color: safePage === p ? '#4DFFFF' : '#5B6178',
                                        }}
                                    >
                                        {p}
                                    </button>
                                )
                            )}
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={safePage === totalPages}
                                style={{
                                    border: 'none', borderRadius: 8, padding: '4px 10px',
                                    fontSize: 13, fontWeight: 600, cursor: safePage === totalPages ? 'default' : 'pointer',
                                    backgroundColor: '#F1F2F6',
                                    color: safePage === totalPages ? '#C8CBDA' : '#5B6178',
                                }}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať komponenty</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Naozaj chcete vymazať <strong>{selectedIds.size} {selectedIds.size === 1 ? 'komponent' : selectedIds.size < 5 ? 'komponenty' : 'komponentov'}</strong>? Táto akcia sa nedá vrátiť.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>Zrušiť</Button>
                    <Button variant="danger" disabled={bulkDeleting} onClick={handleBulkDelete}>
                        {bulkDeleting ? 'Vymazávam...' : 'Vymazať'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!componentToDelete} onHide={() => setComponentToDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Vymazať komponent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Naozaj chcete vymazať komponent <strong>{componentToDelete?.component}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setComponentToDelete(null)}>Zrušiť</Button>
                    <Button variant="danger" onClick={handleDeleteComponent}>Vymazať</Button>
                </Modal.Footer>
            </Modal>

            <CustomModalLarge isVisible={isAddClientModalVisible} onClose={() => setIsAddClientModalVisible(false)}>
                <AddClient
                    isVisible={isAddClientModalVisible}
                    onClose={() => setIsAddClientModalVisible(false)}
                    onSuccess={handleClientCreated}
                    initialCategories={['PACKAGING']}
                />
            </CustomModalLarge>
        </div>
    );
};

export default Overview;
